import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  Box,
  Paper,
  Grid,
  Slider,
  Button,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import { grey } from "@mui/material/colors";

const colors = {
  tealBlue: "#008080",
  lightBlue: "#ADD8E6",
  lightGray: "#F5F5F5",
  white: "#FFFFFF",
  darkText: "#333333",
};

const calculatorCategories = {
  interest: {
    name: "ब्याज और निवेश कैलकुलेटर",
    calculators: ["compoundInterest", "sip", "lumpsum", "emi"],
  },
  savings: {
    name: "बचत और सेवानिवृत्ति कैलकुलेटर",
    calculators: ["epf", "ppf", "nps", "ssa", "postOffice"],
  },
  tax: {
    name: "टैक्स और वेल्थ कैलकुलेटर",
    calculators: ["elss", "croreToMillion", "mis"],
  },
};

const calculatorConfig = {
  compoundInterest: {
    name: "चक्रवृद्धि ब्याज",
    amountLabel: "मूलधन (₹)",
    interestLabel: "वार्षिक ब्याज दर (%)",
    durationLabel: "समय (वर्ष)",
    frequencyLabel: "चक्रवृद्धि आवृत्ति",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "चक्रवृद्धि ब्याज कैलकुलेटर",
    resultLabel: "मिश्रधन",
    breakdown: (amount, rate, duration, result) => [
      { name: "मूलधन", value: amount },
      { name: "ब्याज", value: result.interest },
    ],
    calculate: (p, r, t, n = 1) => {
      const total = p * Math.pow(1 + r / (n * 100), n * t);
      return { total, interest: total - p };
    },
    generateYearlyData: (p, r, t, n = 1) => {
      const data = [];
      for (let year = 1; year <= t; year++) {
        const total = p * Math.pow(1 + r / (n * 100), n * year);
        data.push({
          year,
          principal: p,
          interest: total - p,
          total,
        });
      }
      return data;
    },
  },
  percentage: {
    name: "प्रतिशत निकालें",
    amountLabel: "मूल राशि (₹)",
    percentageLabel: "प्रतिशत (%)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "प्रतिशत कैलकुलेटर",
    resultLabel: "परिणाम",
    breakdown: (amount, percent, result) => [
      { name: "मूल राशि", value: amount },
      /*{ name: "प्रतिशत", value: result.value },*/
    ],
    calculate: (amount, percent) => ({ value: (amount * percent) / 100 }),
  },
  sip: {
    name: "SIP कैलकुलेटर",
    amountLabel: "मासिक निवेश (₹)",
    interestLabel: "अपेक्षित रिटर्न दर (%)",
    durationLabel: "निवेश अवधि (वर्ष)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "सिप कैलकुलेटर",
    resultLabel: "परिपक्वता मूल्य",
    breakdown: (amount, rate, duration, result) => [
      { name: "कुल निवेश", value: result.invested },
      { name: "कुल ब्याज", value: result.interest },
    ],
    calculate: (p, r, t) => {
      const n = t * 12;
      const monthlyRate = r / 100 / 12;
      const total =
        p *
        ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate) *
        (1 + monthlyRate);
      const invested = p * n;
      return { total, invested, interest: total - invested };
    },
  },
  lumpsum: {
    name: "एकमुश्त निवेश",
    amountLabel: "निवेश राशि (₹)",
    interestLabel: "अपेक्षित रिटर्न दर (%)",
    durationLabel: "निवेश अवधि (वर्ष)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "एकमुश्त निवेश कैलकुलेटर",
    resultLabel: "परिपक्वता मूल्य",
    breakdown: (amount, rate, duration, result) => [
      { name: "प्रारंभिक राशि", value: amount },
      { name: "कुल योगदान", value: amount },
      { name: "ब्याज", value: result.interest },
    ],
    calculate: (p, r, t) => {
      const total = p * Math.pow(1 + r / 100, t);
      return { total, interest: total - p };
    },
  },
  emi: {
    name: "EMI कैलकुलेटर",
    amountLabel: "ऋण राशि (₹)",
    interestLabel: "वार्षिक ब्याज दर (%)",
    durationLabel: "ऋण अवधि (वर्ष)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "ईएमआई कैलकुलेटर",
    resultLabel: "मासिक ईएमआई",
    breakdown: (amount, rate, duration, result) => [
      { name: "मूल ऋण", value: amount },
      { name: "कुल ब्याज", value: result.totalInterest },
    ],
    calculate: (p, r, t) => {
      const monthlyRate = r / 100 / 12;
      const n = t * 12;
      const emi =
        (p * monthlyRate * Math.pow(1 + monthlyRate, n)) /
        (Math.pow(1 + monthlyRate, n) - 1);
      const totalPayment = emi * n;
      return { emi, totalPayment, totalInterest: totalPayment - p };
    },
  },
  epf: {
    name: "EPF और VPF कैलकुलेटर",
    amountLabel: "मूल वेतन (₹)",
    interestLabel: "ईपीएफ ब्याज दर (%)",
    durationLabel: "निवेश अवधि (वर्ष)",
    employeeContributionLabel: "कर्मचारी योगदान (%)",
    employerContributionLabel: "नियोक्ता योगदान (%)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "ईपीएफ कैलकुलेटर",
    resultLabel: "परिपक्वता राशि",
    breakdown: (amount, rate, duration, result) => [
      { name: "कुल योगदान", value: result.totalContribution },
      { name: "कुल ब्याज", value: result.totalInterest },
    ],
    calculate: (p, r, t, empCont = 12, emprCont = 12) => {
      const monthlyEmpCont = p * (empCont / 100);
      const monthlyEmprCont = p * (emprCont / 100);
      const totalMonthly = monthlyEmpCont + monthlyEmprCont;
      const n = t * 12;
      const monthlyRate = r / 100 / 12;
      const total =
        totalMonthly * ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate);
      const totalContribution = totalMonthly * n;
      return {
        total,
        totalContribution,
        totalInterest: total - totalContribution,
      };
    },
  },
  ppf: {
    name: "PPF कैलकुलेटर",
    amountLabel: "वार्षिक निवेश (₹)",
    interestLabel: "पीपीएफ ब्याज दर (%)",
    durationLabel: "निवेश अवधि (वर्ष)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "पीपीएफ कैलकुलेटर",
    resultLabel: "परिपक्वता राशि",
    breakdown: (amount, rate, duration, result) => [
      { name: "कुल निवेश", value: result.totalInvestment },
      { name: "कुल ब्याज", value: result.totalInterest },
    ],
    calculate: (p, r, t) => {
      let total = 0;
      for (let i = 1; i <= t; i++) {
        total = (total + p) * (1 + r / 100);
      }
      return { total, totalInvestment: p * t, totalInterest: total - p * t };
    },
  },
  nps: {
    name: "NPS कैलकुलेटर",
    amountLabel: "वार्षिक निवेश (₹)",
    interestLabel: "अपेक्षित रिटर्न दर (%)",
    durationLabel: "निवेश अवधि (वर्ष)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "एनपीएस कैलकुलेटर",
    resultLabel: "परिपक्वता राशि",
    breakdown: (amount, rate, duration, result) => [
      { name: "कुल निवेश", value: result.totalInvestment },
      { name: "कुल रिटर्न", value: result.totalReturn },
    ],
    calculate: (p, r, t) => {
      const monthlyInvestment = p / 12;
      const n = t * 12;
      const monthlyRate = r / 100 / 12;
      const total =
        monthlyInvestment * ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate);
      return { total, totalInvestment: p * t, totalReturn: total - p * t };
    },
  },
  ssa: {
    name: "SSA कैलकुलेटर",
    amountLabel: "मासिक निवेश (₹)",
    interestLabel: "ब्याज दर (%)",
    durationLabel: "लड़की की उम्र (वर्ष)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "सुकन्या समृद्धि कैलकुलेटर",
    resultLabel: "परिपक्वता राशि",
    breakdown: (amount, rate, duration, result) => [
      { name: "कुल निवेश", value: result.totalInvestment },
      { name: "कुल ब्याज", value: result.totalInterest },
    ],
    calculate: (p, r, t) => {
      const n = t * 12;
      const quarterlyRate = r / 100 / 4;
      let total = 0;
      for (let i = 0; i < n; i++) {
        total = (total + p) * Math.pow(1 + quarterlyRate, 1 / 3);
      }
      return { total, totalInvestment: p * n, totalInterest: total - p * n };
    },
  },
  postOffice: {
    name: "डाकघर बचत",
    amountLabel: "मासिक निवेश (₹)",
    interestLabel: "ब्याज दर (%)",
    durationLabel: "निवेश अवधि (वर्ष)",
    schemeLabel: "डाकघर योजना",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "डाकघर बचत कैलकुलेटर",
    resultLabel: "परिपक्वता राशि",
    breakdown: (amount, rate, duration, result) => [
      { name: "कुल निवेश", value: result.totalInvestment },
      { name: "कुल ब्याज", value: result.totalInterest },
    ],
    calculate: (p, r, t, scheme) => {
      // Define different interest rates for different schemes
      const schemeRates = {
        rd: 6.7, // Recurring Deposit
        td: 7.5, // Time Deposit
        ssa: 7.6, // Sukanya Samriddhi
        scss: 8.2, // Senior Citizen Savings
        nsc: 7.7, // National Savings Certificate
      };

      // Use the scheme-specific rate if provided, otherwise use the user input rate
      const effectiveRate = scheme ? schemeRates[scheme] : r;

      const n = t * 12;
      const quarterlyRate = effectiveRate / 100 / 4;
      let total = 0;
      for (let i = 0; i < n; i++) {
        total = (total + p) * Math.pow(1 + quarterlyRate, 1 / 3);
      }
      return {
        total,
        totalInvestment: p * n,
        totalInterest: total - p * n,
      };
    },
  },
  elss: {
    name: "ELSS कैलकुलेटर",
    amountLabel: "वार्षिक निवेश (₹)",
    interestLabel: "अपेक्षित रिटर्न दर (%)",
    durationLabel: "निवेश अवधि (वर्ष)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "ईएलएसएस कैलकुलेटर",
    resultLabel: "परिपक्वता मूल्य",
    breakdown: (amount, rate, duration, result) => [
      { name: "कुल निवेश", value: result.totalInvestment },
      { name: "कुल रिटर्न", value: result.totalReturn },
    ],
    calculate: (p, r, t) => {
      const total = p * ((Math.pow(1 + r / 100, t) - 1) / (r / 100));
      return { total, totalInvestment: p * t, totalReturn: total - p * t };
    },
  },
  croreToMillion: {
    name: "करोड़ से मिलियन",
    amountLabel: "राशि (₹ करोड़ में)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "करोड़ से मिलियन कनवर्टर",
    resultLabel: "मिलियन में मूल्य",
    breakdown: (amount, result) => [
      { name: "मिलियन", value: amount * 10 },
      /*{ name: "मिलियन", value: result.millions },*/
    ],
    calculate: (amount) => {
      const exchangeRate = 0.012;
      return {
        millions: amount * 10,
        usd: amount * 10 * exchangeRate,
      };
    },
  },
  mis: {
    name: "MIS कैलकुलेटर",
    amountLabel: "निवेश राशि (₹)",
    interestLabel: "ब्याज दर (%)",
    durationLabel: "निवेश अवधि (महीने)",
    chartColors: ["#008080", "#ADD8E6", "#DD6B20"],
    calculationType: "मासिक आय योजना कैलकुलेटर",
    resultLabel: "मासिक आय",
    breakdown: (amount, rate, duration, result) => [
      { name: "निवेश", value: amount },
      { name: "कुल ब्याज", value: result.totalInterest },
    ],
    calculate: (p, r, t) => {
      const monthlyIncome = (p * r) / 100 / 12;
      return { monthlyIncome, totalInterest: monthlyIncome * t };
    },
  },
};

const frequencyOptions = [
  { value: 1, label: "वार्षिक" },
  { value: 2, label: "अर्धवार्षिक" },
  { value: 4, label: "त्रैमासिक" },
  { value: 12, label: "मासिक" },
];

const postOfficeSchemes = [
  { value: "rd", label: "आवर्ती जमा" },
  { value: "td", label: "सावधि जमा" },
  { value: "ssa", label: "सुकन्या समृद्धि" },
  { value: "scss", label: "वरिष्ठ नागरिक बचत योजना" },
  { value: "nsc", label: "राष्ट्रीय बचत प्रमाणपत्र" },
];

const FinancialCalculator = () => {
  const [category, setCategory] = useState("interest");
  const [calculatorType, setCalculatorType] = useState("compoundInterest");
  const [amount, setAmount] = useState(100000);
  const [rate, setRate] = useState(8);
  const [duration, setDuration] = useState(5);
  const [frequency, setFrequency] = useState(1);
  const [deductions, setDeductions] = useState(50000);
  const [percentage, setPercentage] = useState(10);
  const [employeeContribution, setEmployeeContribution] = useState(12);
  const [employerContribution, setEmployerContribution] = useState(12);
  const [postOfficeScheme, setPostOfficeScheme] = useState("rd");
  const [ageGroup, setAgeGroup] = useState("general");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const config = calculatorConfig[calculatorType];
  const calculateResult = () => {
    switch (calculatorType) {
      case "compoundInterest":
        return {
          ...config.calculate(amount, rate, duration, frequency),
          yearlyData: config.generateYearlyData(
            amount,
            rate,
            duration,
            frequency
          ),
        };
      case "percentage":
        return config.calculate(amount, percentage);
      case "epf":
        return config.calculate(
          amount,
          rate,
          duration,
          employeeContribution,
          employerContribution
        );
      case "postOffice":
        return config.calculate(amount, rate, duration, postOfficeScheme);
      default:
        return config.calculate(amount, rate, duration);
    }
  };

  const result = calculateResult();
  const pieData = config.breakdown(
    amount,
    rate,
    duration,
    result,
    deductions,
    percentage,
    employeeContribution,
    employerContribution
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getButtonLabel = (type) => {
    const labels = {
      emi: "ऋण के लिए आवेदन करें",
      sip: "निवेश करना चाहते हैं?",
      lumpsum: "निवेश करना चाहते हैं?",
      ppf: "निवेश करना चाहते हैं?",
      nps: "निवेश करना चाहते हैं?",
      ssa: "निवेश करना चाहते हैं?",
      postOffice: "निवेश करना चाहते हैं?",
      elss: "निवेश करना चाहते हैं?",
      epf: "EPF खाता खोलें",
      compoundInterest: "ऋण के लिए आवेदन करें",
    };
    return labels[type] || null;
  };

  const renderChart = () => {
    if (config.showGraph === false) {
      return null;
    }

    if (calculatorType === "compoundInterest") {
      return (
        <Box
          sx={{
            position: "relative",
            width: "80%",
            maxWidth: "300px",
            height: "300px",
            my: 2,
            filter: "drop-shadow(12px 4px 8px rgba(0, 0, 0, 0.2))",
            marginLeft: isMobile ? "0" : "20px",
          }}
        >
          <PieChart width={300} height={300}>
            <Pie
              data={pieData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius={135}
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
              label={false}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    index === 0
                      ? "#4fd1d9"
                      : index === 1
                      ? "#ffb300"
                      : "#7ed957"
                  }
                />
              ))}
            </Pie>
          </PieChart>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "300px",
          height: "300px",
          my: 4,
          marginTop: calculatorType === "lumpsum" ? "0px" : "-70px",
          filter: "drop-shadow(24px 4px 8px rgba(0, 0, 0, 0.2))",
        }}
      >
        <PieChart width={300} height={300}>
          <Pie
            data={pieData}
            dataKey="value"
            cx="50%"
            cy="50%"
            innerRadius={calculatorType === "lumpsum" ? 90 : 130}
            outerRadius={calculatorType === "lumpsum" ? 120 : 150}
            paddingAngle={2}
            startAngle={90}
            endAngle={-270}
            label={false}
          >
            {pieData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  index === 0 ? "#4fd1d9" : index === 1 ? "#ffb300" : "#7ed957"
                }
              />
            ))}
          </Pie>
        </PieChart>

        {calculatorType !== "lumpsum" && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              color: "#fff",
              width: "100%",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: "'Lato', sans-serif",
              }}
            >
              ₹
              {formatCurrency(
                result.total ||
                  result.tax ||
                  result.value ||
                  result.emi ||
                  result.monthlyIncome ||
                  result.millions ||
                  result.totalPayment ||
                  0
              )}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 300,
                fontFamily: "'Lato', sans-serif",
              }}
            >
              {calculatorType === "emi" ? "कुल देय" : "कुल राशि"}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderMenu = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {Object.values(calculatorCategories).flatMap((category) =>
        category.calculators.map((calc) => (
          <Box
            key={calc}
            sx={{
              padding: "12px 16px",
              cursor: "pointer",
              borderRadius: "4px",
              backgroundColor:
                calculatorType === calc ? "#e3f2fd" : "transparent",
              color: calculatorType === calc ? "#1976d2" : "inherit",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
              fontFamily: "'Lato', sans-serif",
            }}
            onClick={() => {
              setCalculatorType(calc);
              setMobileMenuOpen(false);
            }}
          >
            {calculatorConfig[calc]?.name || calc}
          </Box>
        ))
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: colors.lightGray,
        minHeight: "100vh",
        py: 4,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        fontFamily: "'Lato', sans-serif",
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            width: "250px",
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            marginRight: "20px",
            padding: "16px",
            position: "relative",
            top: "20px",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 600, fontFamily: "'Lato', sans-serif" }}
          >
            Calculators
          </Typography>
          {renderMenu()}
        </Box>
      )}

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "250px",
            padding: "16px",
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, fontFamily: "'Lato', sans-serif" }}
        >
          Calculators
        </Typography>
        {renderMenu()}
      </Drawer>

      <Box
        sx={{
          flex: 1,
          maxWidth: "1000px",
          width: isMobile ? "90%" : "50%",
          px: isMobile ? 2 : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {isMobile && (
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                color: colors.tealBlue,
                backgroundColor: colors.white,
                "&:hover": { backgroundColor: colors.lightGray },
                mr: 2,
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h4"
            align="left"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: colors.tealBlue,
              fontSize: { xs: "1.8rem", md: "2.125rem" },
              fontFamily: "'Lato', sans-serif",
              flex: 1,
            }}
          >
            {calculatorConfig[calculatorType]?.name || "Calculator"}
          </Typography>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card
            sx={{
              width: "100%",
              p: 3,
              boxShadow: "0 4px 20px rgba(0, 128, 128, 0.2)",
              borderRadius: 2,
              backgroundColor: colors.white,
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          fontFamily: "'Lato', sans-serif",
                        }}
                      >
                        {config.amountLabel}
                      </Typography>
                      <TextField
                        fullWidth
                        value={amount}
                        onChange={(e) => setAmount(Number(e.target.value))}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          style: {
                            appearance: "textfield",
                            MozAppearance: "textfield",
                            fontFamily: "'Lato', sans-serif",
                          },
                        }}
                        sx={{
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                            {
                              display: "none",
                            },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "1px",
                            backgroundColor: colors.lightGray,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#7a9fd1",
                            },
                          },
                          mb: 1,
                        }}
                      />
                      <Slider
                        value={amount}
                        onChange={(e, newValue) => setAmount(newValue)}
                        min={1000}
                        max={10000000}
                        step={1000}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) =>
                          `₹${formatCurrency(value)}`
                        }
                        sx={{
                          "& .MuiSlider-thumb": {
                            color: "#7ed957",
                          },
                          "& .MuiSlider-track": {
                            background:
                              "linear-gradient(90deg, #0097b2, #7ed957)",
                            border: "none",
                          },
                          "& .MuiSlider-rail": {
                            color: colors.lightGray,
                          },
                          "& .MuiSlider-valueLabel": {
                            background:
                              "linear-gradient(90deg, #0097b2, #7ed957)",
                            color: colors.white,
                            fontFamily: "'Lato', sans-serif",
                          },
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontFamily: "'Lato', sans-serif",
                          fontSize: "1.0rem",
                        }}
                      >
                        स्लाइडर प्रत्येक बार ₹ 1000 बढ़ता है।
                      </Typography>
                      <Box
                        sx={{
                          borderBottom: "1px solid #000",
                          width: "100%",
                          height: "20px",
                          mb: 2,
                          color: "grey",
                        }}
                      />
                    </Box>

                    {calculatorType !== "percentage" &&
                      calculatorType !== "croreToMillion" && (
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              fontFamily: "'Lato', sans-serif",
                            }}
                          >
                            {config.interestLabel}
                          </Typography>
                          <TextField
                            fullWidth
                            value={rate}
                            onChange={(e) => setRate(Number(e.target.value))}
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              style: {
                                appearance: "textfield",
                                MozAppearance: "textfield",
                                fontFamily: "'Lato', sans-serif",
                              },
                            }}
                            sx={{
                              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                {
                                  display: "none",
                                },
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                backgroundColor: colors.lightGray,
                              },
                              mb: 1,
                            }}
                          />
                          <Slider
                            value={rate}
                            onChange={(e, newValue) => setRate(newValue)}
                            min={1}
                            max={30}
                            step={0.1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value}%`}
                            sx={{
                              "& .MuiSlider-thumb": {
                                color: "#7ed957",
                              },
                              "& .MuiSlider-track": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                border: "none",
                              },
                              "& .MuiSlider-rail": {
                                color: colors.lightGray,
                              },
                              "& .MuiSlider-valueLabel": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                color: colors.white,
                                fontFamily: "'Lato', sans-serif",
                              },
                            }}
                          />
                        </Box>
                      )}

                    {calculatorType !== "percentage" &&
                      calculatorType !== "croreToMillion" && (
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              fontFamily: "'Lato', sans-serif",
                            }}
                          >
                            {config.durationLabel}
                          </Typography>
                          <TextField
                            fullWidth
                            value={duration}
                            onChange={(e) =>
                              setDuration(Number(e.target.value))
                            }
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              style: {
                                appearance: "textfield",
                                MozAppearance: "textfield",
                                fontFamily: "'Lato', sans-serif",
                              },
                            }}
                            sx={{
                              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                {
                                  display: "none",
                                },
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                backgroundColor: colors.lightGray,
                              },
                              mb: 1,
                            }}
                          />
                          <Slider
                            value={duration}
                            onChange={(e, newValue) => setDuration(newValue)}
                            min={1}
                            max={calculatorType === "mis" ? 60 : 30}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) =>
                              `${value} ${
                                calculatorType === "mis" ? "महीने" : "वर्ष"
                              }`
                            }
                            sx={{
                              "& .MuiSlider-thumb": {
                                color: "#7ed957",
                              },
                              "& .MuiSlider-track": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                border: "none",
                              },
                              "& .MuiSlider-rail": {
                                color: colors.lightGray,
                              },
                              "& .MuiSlider-valueLabel": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                color: colors.white,
                                fontFamily: "'Lato', sans-serif",
                              },
                            }}
                          />
                        </Box>
                      )}

                    {calculatorType === "percentage" && (
                      <Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            fontFamily: "'Lato', sans-serif",
                          }}
                        >
                          {config.percentageLabel}
                        </Typography>
                        <TextField
                          fullWidth
                          value={percentage}
                          onChange={(e) =>
                            setPercentage(Number(e.target.value))
                          }
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            style: {
                              appearance: "textfield",
                              MozAppearance: "textfield",
                              fontFamily: "'Lato', sans-serif",
                            },
                          }}
                          sx={{
                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                              {
                                display: "none",
                              },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: colors.lightGray,
                            },
                            mb: 1,
                          }}
                        />
                        <Slider
                          value={percentage}
                          onChange={(e, newValue) => setPercentage(newValue)}
                          min={1}
                          max={100}
                          step={1}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${value}%`}
                          sx={{
                            "& .MuiSlider-thumb": {
                              color: "#7ed957",
                            },
                            "& .MuiSlider-track": {
                              background:
                                "linear-gradient(90deg, #0097b2, #7ed957)",
                              border: "none",
                            },
                            "& .MuiSlider-rail": {
                              color: colors.lightGray,
                            },
                            "& .MuiSlider-valueLabel": {
                              background:
                                "linear-gradient(90deg, #0097b2, #7ed957)",
                              color: colors.white,
                              fontFamily: "'Lato', sans-serif",
                            },
                          }}
                        />
                      </Box>
                    )}

                    {calculatorType === "epf" && (
                      <>
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              fontFamily: "'Lato', sans-serif",
                            }}
                          >
                            {config.employeeContributionLabel}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              value={employeeContribution}
                              onChange={(e) =>
                                setEmployeeContribution(Number(e.target.value))
                              }
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                style: {
                                  appearance: "textfield",
                                  MozAppearance: "textfield",
                                  fontFamily: "'Lato', sans-serif",
                                },
                              }}
                              sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                  {
                                    display: "none",
                                  },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: colors.lightGray,
                                },
                              }}
                            />
                          </Box>
                          <Slider
                            value={employeeContribution}
                            onChange={(e, newValue) =>
                              setEmployeeContribution(newValue)
                            }
                            min={0}
                            max={100}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value}%`}
                            sx={{
                              "& .MuiSlider-thumb": {
                                color: "#7ed957",
                              },
                              "& .MuiSlider-track": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                border: "none",
                              },
                              "& .MuiSlider-rail": {
                                color: colors.lightGray,
                              },
                              "& .MuiSlider-valueLabel": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                color: colors.white,
                                fontFamily: "'Lato', sans-serif",
                              },
                            }}
                          />
                        </Box>

                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              fontFamily: "'Lato', sans-serif",
                            }}
                          >
                            {config.employerContributionLabel}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              value={employerContribution}
                              onChange={(e) =>
                                setEmployerContribution(Number(e.target.value))
                              }
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                style: {
                                  appearance: "textfield",
                                  MozAppearance: "textfield",
                                  fontFamily: "'Lato', sans-serif",
                                },
                              }}
                              sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                  {
                                    display: "none",
                                  },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: colors.lightGray,
                                },
                              }}
                            />
                          </Box>
                          <Slider
                            value={employerContribution}
                            onChange={(e, newValue) =>
                              setEmployerContribution(newValue)
                            }
                            min={0}
                            max={100}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value}%`}
                            sx={{
                              "& .MuiSlider-thumb": {
                                color: "#7ed957",
                              },
                              "& .MuiSlider-track": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                border: "none",
                              },
                              "& .MuiSlider-rail": {
                                color: colors.lightGray,
                              },
                              "& .MuiSlider-valueLabel": {
                                background:
                                  "linear-gradient(90deg, #0097b2, #7ed957)",
                                color: colors.white,
                                fontFamily: "'Lato', sans-serif",
                              },
                            }}
                          />
                        </Box>
                      </>
                    )}

                    {calculatorType === "postOffice" && (
                      <Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            fontFamily: "'Lato', sans-serif",
                          }}
                        >
                          {config.schemeLabel}
                        </Typography>
                        <FormControl fullWidth>
                          <TextField
                            select
                            value={postOfficeScheme}
                            onChange={(e) =>
                              setPostOfficeScheme(e.target.value)
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                backgroundColor: colors.lightGray,
                              },
                            }}
                          >
                            {postOfficeSchemes.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{ fontFamily: "'Lato', sans-serif" }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 3,
                      height: "100%",
                      minHeight: calculatorType === "epf" ? "950px" : "850px",
                      width: isMobile ? "100%" : "calc(70% + 100px)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "linear-gradient(90deg, #0097b2, #7ed957)",
                      boxShadow: "none",
                      color: "#ffffff",
                      marginLeft: isMobile ? -2.5 : "77px",
                      marginRight: isMobile ? 5 : "0px",
                      marginTop: isMobile ? 2 : "-40px",
                      marginBottom: isMobile ? 0 : "-50px",
                      position: "relative",
                    }}
                  >
                    <Box sx={{ width: "100%", pt: 2 }}>
                      <Typography
                        variant="h5"
                        align="center"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          mb: 3,
                          color: "#ffffff",
                          fontSize: "1.5rem",
                          fontFamily: "'Lato', sans-serif",
                          marginLeft: "-15px",
                        }}
                      >
                        {calculatorConfig[calculatorType].resultLabel}
                      </Typography>

                      <Typography
                        variant="h5"
                        align="center"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          mb: 3,
                          color: "#ffffff",
                          fontSize: "2.0rem",
                          fontFamily: "'Lato', sans-serif",
                          marginLeft: "-15px",
                        }}
                      >
                        ₹
                        {typeof result === "object"
                          ? formatCurrency(
                              result.total ||
                                result.tax ||
                                result.value ||
                                result.emi ||
                                result.monthlyIncome ||
                                result.millions ||
                                result.totalPayment ||
                                0
                            )
                          : result.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        marginLeft: isMobile
                          ? calculatorType === "compoundInterest"
                            ? "0px"
                            : "0px"
                          : calculatorType === "compoundInterest"
                          ? "-20px"
                          : "0px",
                      }}
                    >
                      {renderChart()}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: "80%",
                        mb: 3,
                        marginTop: isMobile ? 2 : "-40px",
                      }}
                    >
                      {pieData.map((entry, index) => (
                        <Box
                          key={index}
                          sx={{
                            backgroundColor: "white",
                            width: "100%",
                            maxWidth: "800px",
                            padding: "16px",
                            borderRadius: "50px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            marginLeft: "-15px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                              minWidth: "fit-content",
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "50%",
                                backgroundColor:
                                  index === 0 ? "#4fd1d9" : "#ffb300",
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body1"
                              sx={{
                                color: "#000000",
                                fontSize: "1.2rem",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                                flexGrow: 1,
                                fontFamily: "'Lato', sans-serif",
                              }}
                            >
                              <span>{entry.name} :</span>
                              <span>
                                {" "}
                                {"  "}
                                {"  "}
                                {"                   "}₹
                                {formatCurrency(entry.value)}
                              </span>
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    {getButtonLabel(calculatorType) && (
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "#ffffff",
                          color: "#0097b2",
                          borderRadius: "8px",
                          px: 4,
                          py: 1.8,
                          fontWeight: 700,
                          fontSize: "1.1rem",
                          marginLeft: isMobile ? 0 : "20px",
                          width: isMobile ? "75%" : "65%",
                          maxWidth: "300px",
                          marginTop: isMobile ? 2 : "-40px",
                          fontFamily: "'Lato', sans-serif",
                          position: "relative",
                          boxShadow: "12px 12px 18px rgba(0, 151, 178, 0.15)",
                          border: "2px solid #e0e0e0",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#f8f8f8",
                            boxShadow: "0 6px 16px rgba(0, 151, 178, 0.2)",
                            pr: 6,
                            transform: "translateY(-2px)",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: "24px",
                              height: "24px",
                              backgroundColor: "#00C853",
                              borderRadius: "50%",
                              transition: "all 0.3s ease",
                            },
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              right: "20px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: "0",
                              height: "0",
                              borderTop: "8px solid transparent",
                              borderBottom: "8px solid transparent",
                              borderLeft: "8px solid white",
                              transition: "all 0.3s ease",
                            },
                          },
                          transition:
                            "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            letterSpacing: "0.5px",
                            transition: "transform 0.3s ease",
                            fontFamily: "'Lato', sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          {getButtonLabel(calculatorType)}
                        </Typography>
                      </Button>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </motion.div>
      </Box>
    </Box>
  );
};

export default FinancialCalculator;
